import { axiosClient } from "@/shared/api/axios-client";

// ============================================================================
// Contrato del módulo de Notificaciones (backend: NotificacionesEndpoints).
// Los enums viajan como texto (Email, Fijo, Igual…).
// ============================================================================

export type Canal = "Email" | "Campana" | "WebPush" | "Sms" | "WhatsApp" | "Telegram" | "Webhook";

export type TipoDestinatario =
  | "Fijo"
  | "SolicitanteODueno"
  | "AutorizadoresDelNivel"
  | "UsuariosConRol"
  | "Actor"
  | "UsuarioEspecifico"
  | "ResponsablesDelTipoCatalogo"
  | "RevisoresDocumentales";

export type TipoDeVariable = "Texto" | "Numero" | "Fecha" | "FechaHora" | "Booleano" | "Lista" | "Identificador";

export type OperadorComparacion =
  | "Igual" | "Distinto" | "Mayor" | "MayorOIgual" | "Menor" | "MenorOIgual"
  | "Contiene" | "NoContiene" | "EmpiezaCon" | "EnLista" | "NoEnLista" | "Vacio" | "NoVacio";

export interface VariableDeEvento {
  nombre: string;
  tipo: TipoDeVariable;
  /** "evento" (propiedad del evento) o "enriquecida" (la calcula el motor). */
  origen: "evento" | "enriquecida";
  descripcion: string | null;
}

export interface EventoDelCatalogo {
  codigo: string;
  nombre: string;
  categoria: string;
  descripcion: string | null;
  variables: VariableDeEvento[];
}

export interface CatalogoNotificaciones {
  eventos: EventoDelCatalogo[];
  canales: Canal[];
  tiposDeDestinatario: TipoDestinatario[];
  formatos: string[];
}

export interface Destinatario {
  tipo: TipoDestinatario;
  valor?: string | null;
  soloOrganizacionDelEvento?: boolean;
}

export interface EncabezadoWebhook {
  nombre: string;
  valor: string;
}

export interface Notificacion {
  clave: string;
  canal: Canal;
  nombre: string;
  descripcion: string | null;
  activa: boolean;
  obligatoria: boolean;
  destinatarios: Destinatario[];
  asunto?: string | null;
  cuerpoHtml?: string | null;
  url?: string | null;
  cuerpoJson?: string | null;
  encabezados?: EncabezadoWebhook[] | null;
  titulo?: string | null;
  texto?: string | null;
  enlace?: string | null;
  /** Web push. */
  cuerpo?: string | null;
  /** Telegram · el texto fijo va en MarkdownV2. */
  usarMarkdown?: boolean;
  /** WhatsApp · plantilla aprobada por Meta (HX…) y valor de cada marcador {{1}}, {{2}}… */
  contentSid?: string | null;
  variablesWhatsApp?: Record<string, string> | null;
}

export interface ReglaDeCondicion {
  campo: string;
  operador: OperadorComparacion;
  valor: string | null;
}

export interface Condicion {
  operador: "Y" | "O";
  reglas: ReglaDeCondicion[];
  grupos?: Condicion[] | null;
}

export interface NodoEvento {
  clave: string;
  tipoEvento: string;
}

export interface Conexion {
  clave: string;
  eventoClave: string;
  notificacionClave: string;
  activa: boolean;
  condicion: Condicion | null;
}

export interface VersionDeFlujo {
  id: string;
  numero: number;
  estado: "Borrador" | "Publicada" | "Retirada";
  publicadaAt: string | null;
  publicadaPorUsuarioId: string | null;
  layoutJson: string | null;
  eventos: NodoEvento[];
  notificaciones: Notificacion[];
  conexiones: Conexion[];
}

export interface FlujoResumen {
  id: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  esDeSistema: boolean;
  versionVigente: number | null;
  publicadaAt: string | null;
  borradorConCambios: boolean;
  eventos: string[];
  notificaciones: number;
  actualizadoAt: string;
}

export interface FlujoDetalle {
  flujo: FlujoResumen;
  borrador: VersionDeFlujo;
  vigente: VersionDeFlujo | null;
  erroresDelBorrador: string[];
}

export interface VersionResumen {
  id: string;
  numero: number;
  estado: string;
  publicadaAt: string | null;
  publicadaPorUsuarioId: string | null;
}

export interface Pagina<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type EstadoEnvio =
  | "Pendiente" | "Enviando" | "Enviado" | "Entregado" | "Leido" | "Reintentando" | "Fallido" | "Omitido" | "NoEntregado";

export interface EnvioResumen {
  id: string;
  creadoAt: string;
  tipoEvento: string;
  flujoId: string;
  versionFlujo: number;
  notificacionNombre: string;
  canal: Canal;
  destinatario: string;
  destinatarioNombre: string | null;
  estado: EstadoEnvio;
  intentos: number;
  proximoIntentoAt: string | null;
  ultimoError: string | null;
  motivoOmision: string | null;
  esPrueba: boolean;
  enviadoAt: string | null;
}

export interface IntentoDeEnvio {
  numero: number;
  at: string;
  exitoso: boolean;
  mensaje: string | null;
}

export interface EnvioDetalle {
  envio: EnvioResumen;
  eventoId: string;
  notificacionClave: string;
  origenDestinatario: string;
  contenido: Record<string, string>;
  eventoPayload: string | null;
  proveedorMensajeId: string | null;
  entregadoAt: string | null;
  historial: IntentoDeEnvio[];
}

export interface PaginaEnvios extends Pagina<EnvioResumen> {
  porEstado: Record<string, number>;
}

export interface ListarFlujosParams {
  page?: number;
  pageSize?: number;
  busqueda?: string;
  activo?: boolean;
}

export interface ListarEnviosParams {
  page?: number;
  pageSize?: number;
  flujoId?: string;
  tipoEvento?: string;
  canal?: string;
  estado?: string;
  destinatario?: string;
  desde?: string;
  hasta?: string;
  esPrueba?: boolean;
}

export interface GuardarBorradorInput {
  eventos: NodoEvento[];
  notificaciones: Notificacion[];
  conexiones: Conexion[];
  layoutJson: string | null;
}

export interface VistaPrevia {
  campos: Record<string, string>;
  errores: string[];
}

const BASE = "/api/admin/notificaciones";

export async function obtenerCatalogo(): Promise<CatalogoNotificaciones> {
  const { data } = await axiosClient.get<CatalogoNotificaciones>(`${BASE}/catalogo`);
  return data;
}

export async function listarFlujos(params: ListarFlujosParams = {}): Promise<Pagina<FlujoResumen>> {
  const { data } = await axiosClient.get<Pagina<FlujoResumen>>(`${BASE}/flujos`, { params });
  return data;
}

export async function obtenerFlujo(id: string): Promise<FlujoDetalle> {
  const { data } = await axiosClient.get<FlujoDetalle>(`${BASE}/flujos/${id}`);
  return data;
}

export async function crearFlujo(input: { nombre: string; descripcion: string | null }): Promise<FlujoDetalle> {
  const { data } = await axiosClient.post<FlujoDetalle>(`${BASE}/flujos`, input);
  return data;
}

export async function renombrarFlujo(id: string, input: { nombre: string; descripcion: string | null }): Promise<FlujoResumen> {
  const { data } = await axiosClient.put<FlujoResumen>(`${BASE}/flujos/${id}`, input);
  return data;
}

export async function cambiarActivoFlujo(id: string, activo: boolean): Promise<FlujoResumen> {
  const { data } = await axiosClient.post<FlujoResumen>(`${BASE}/flujos/${id}/activo`, { activo });
  return data;
}

export async function guardarBorrador(id: string, input: GuardarBorradorInput): Promise<FlujoDetalle> {
  const { data } = await axiosClient.put<FlujoDetalle>(`${BASE}/flujos/${id}/borrador`, input);
  return data;
}

export async function publicarFlujo(id: string): Promise<FlujoDetalle> {
  const { data } = await axiosClient.post<FlujoDetalle>(`${BASE}/flujos/${id}/publicar`);
  return data;
}

export async function listarVersiones(id: string): Promise<VersionResumen[]> {
  const { data } = await axiosClient.get<VersionResumen[]>(`${BASE}/flujos/${id}/versiones`);
  return data;
}

export async function obtenerVersion(id: string, numero: number): Promise<VersionDeFlujo> {
  const { data } = await axiosClient.get<VersionDeFlujo>(`${BASE}/flujos/${id}/versiones/${numero}`);
  return data;
}

export async function enviarPrueba(
  id: string,
  input: { notificacionClave: string; destino: string | null; tipoEvento: string | null },
): Promise<EnvioDetalle> {
  const { data } = await axiosClient.post<EnvioDetalle>(`${BASE}/flujos/${id}/prueba`, input);
  return data;
}

export async function vistaPrevia(tipoEvento: string, notificacion: Notificacion): Promise<VistaPrevia> {
  const { data } = await axiosClient.post<VistaPrevia>(`${BASE}/vista-previa`, { tipoEvento, notificacion });
  return data;
}

export async function listarEnvios(params: ListarEnviosParams = {}): Promise<PaginaEnvios> {
  const { data } = await axiosClient.get<PaginaEnvios>(`${BASE}/envios`, { params });
  return data;
}

export async function obtenerEnvio(id: string): Promise<EnvioDetalle> {
  const { data } = await axiosClient.get<EnvioDetalle>(`${BASE}/envios/${id}`);
  return data;
}

export async function reenviarEnvio(id: string): Promise<EnvioDetalle> {
  const { data } = await axiosClient.post<EnvioDetalle>(`${BASE}/envios/${id}/reenviar`);
  return data;
}
